import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';
import { endOfYear, formatISO, startOfMonth, startOfYear, sub } from 'date-fns';
import { Select } from '@icecreamsocial/components';
import { CalendarPicker } from '../../../../presentational';
import { useDashboardFilters } from '../FilterProvider';
import { useField, useFormikContext } from 'formik';

const TODAY = new Date(Date.now());
const last30 = sub(TODAY, { days: 30 });
const last90 = sub(TODAY, { days: 90 });
const last7 = sub(TODAY, { days: 7 });
const monthToDate = startOfMonth(TODAY);
const lastYear = startOfYear(sub(TODAY, { years: 1 }));
const endOfLastYear = endOfYear(lastYear);

const options = [
	{
		label: 'Last 30 Days',
		value: 'last30',
	},
	{
		label: 'Last 90 Days',
		value: 'last90',
	},
	{
		label: 'Last 7 Days',
		value: 'last7',
	},
	{
		label: 'Today',
		value: 'today',
	},
	{
		label: 'Month to date',
		value: 'monthToDate',
	},
	{
		label: 'Last year',
		value: 'lastYear',
	},
	{
		label: 'Custom',
		value: 'custom',
	},
] as const;

export default forwardRef((props, ref) => {
	const { state } = useDashboardFilters();
	const formik = useFormikContext();
	const [dateRange, meta, dateRangeHelpers] = useField('range');
	const [showCustom, setShowCustom] = useState(
		state.range === 'custom' || dateRange.value?.value === 'custom'
	);

	const { fromDate, toDate, range } = state;

	const onChange = (value) => {
		let fromDate,
			toDate = TODAY;

		dateRangeHelpers.setValue(value);
		if (value.value === 'custom') {
			setShowCustom(true);
			return;
		}

		if (value.value === 'last30') {
			fromDate = last30;
		} else if (value.value === 'last90') {
			fromDate = last90;
		} else if (value.value === 'last7') {
			fromDate = last7;
		} else if (value.value === 'today') {
			fromDate = TODAY;
			toDate = TODAY;
		} else if (value.value === 'monthToDate') {
			fromDate = monthToDate;
		} else if (value.value === 'lastYear') {
			fromDate = lastYear;
			toDate = endOfLastYear;
		}
		formik.setFieldValue('fromDate', formatISO(fromDate));
		formik.setFieldValue('toDate', formatISO(toDate));

		setShowCustom(false);
	};

	useEffect(() => {
		const def = options.filter(({ value }) => value === range)?.[0];
		dateRangeHelpers.setValue(def || options[0]);
		// don't want to count this as the input being touched
		dateRangeHelpers.setTouched(false);
	}, [state]);

	useImperativeHandle(ref, () => ({
		clearSelect: () => {
			onChange(options[0]);
		},
	}));

	return (
		<>
			<Select {...dateRange} options={options} onChange={onChange} />
			{showCustom && (
				<CalendarPicker
					fromDate={fromDate}
					fromDateId={'dashboardFromDate'}
					onFromDateChange={formik.handleChange}
					toDate={toDate}
					toDateId={'dashboardToDate'}
					onToDateChange={formik.handleChange}
				/>
			)}
		</>
	);
});
